import jquery                  from "jquery";
import ko                      from 'knockout';
import readyCallbacks          from "@mks/dev/core/utils/ready_callbacks";
import ArrayStore              from "@mks/dev/data/array_store";
import {formatMessage}         from "@mks/dev/localization";
import {localizeKsaLoginAsync} from '@KsaContent/js/Common/Localization';
import KsaLoginConfig          from "@KsaContent/js/Login/KsaLoginConfig";
import {KsaChangePasswordViewModel} from "@KsaContent/js/Login/KsaChangePasswordViewModel";
import {KsaLoginErrorViewModel}     from "@KsaContent/js/Login/KsaLoginErrorViewModel";
import {makePasswordViewButton}     from "@KsaContent/js/Login/Utils";

/** Модель страницы авторизации. */
class KsaLoginViewModel {
    _loginForm;
    _loginDialog;
    _loadPanel;

    constructor() {
        this.name = formatMessage('KsaLoginId.KsAnalytics.Name');
        this.logoUrl = KsaLoginConfig.urls.loginLogo();
        this.authData = KsaLoginConfig.initialLoginModel();
        this.loginError = new KsaLoginErrorViewModel();
        this.changePassword = new KsaChangePasswordViewModel();
        this._setEventHandlers();
    }

    /** Получение параметров диалогового окна авторизации. */
    loginPopupOptions() {
        const self = this;
        return {
            wrapperAttr: {
                class: "ksa-login-popup"
            },
            height: 'auto',
            width: 440,
            maxHeight: 'calc(100vh - 12px)',
            maxWidth: 'calc(100vw - 12px)',
            showTitle: false,
            visible: true,
            hideOnOutsideClick: false,
            focusStateEnabled: false,
            animation: {
                show: {
                    type: 'pop'
                }
            },
            contentTemplate: document.getElementById('ksa-login-popup-content'),
            onInitialized: function (e) {
                self._loginDialog = e.component;
            },
            onShowing: function () {
                if (self.authData.hasError) {
                    setTimeout(function () {
                        self.loginError.showError({
                            errorType: 'auth',
                            errorMessage: self.authData.errorText
                        });
                    });
                }
            },
            onShown: function () {
                setTimeout(function () {
                    if (jquery(':-webkit-autofill').length === 0 && jquery(':autofill').length === 0) {
                        // Использование логина последнего успешного входа в случае отсутствия автозаполнения.
                        const storedLogin = localStorage.getItem('login');
                        if (storedLogin) {
                            self.authData.login = storedLogin;
                            self._loginForm?.repaint();
                        }
                    }
                });
            }
        };
    }

    /** Получение параметров формы авторизации.
     *  @returns {object} параметры формы авторизации. */
    loginFormOptions() {
        const self = this;
        const options = {
            elementAttr: {
                class: 'ksa-login-form'
            },
            scrollingEnabled: true,
            focusStateEnabled: false,
            labelMode: 'outside',
            labelLocation: 'top',
            showColonAfterLabel: false,
            items: [{
                itemType: 'simple',
                dataField: 'login',
                cssClass: 'ksa-login-input',
                visible: !KsaLoginConfig.useDemoMode(),
                label: {
                    text: formatMessage("KsaLoginId.Form.Username")
                },
                editorType: 'dxTextBox',
                editorOptions: {
                    height: 56,
                    inputAttr: {
                        autocomplete: 'username'
                    },
                    onEnterKey: self._loginStandard
                }
            }, {
                itemType: 'simple',
                dataField: 'password',
                cssClass: 'ksa-login-input',
                visible: !KsaLoginConfig.useDemoMode(),
                label: {
                    text: formatMessage("KsaLoginId.Form.Password")
                },
                editorType: 'dxTextBox',
                editorOptions: {
                    height: 56,
                    elementAttr: {
                        class: 'ksa-password-editor'
                    },
                    inputAttr: {
                        autocomplete: 'current-password'
                    },
                    onEnterKey: self._loginStandard,
                    mode: 'password',
                    buttons: [makePasswordViewButton()]
                }
            }, {
                itemType: 'simple',
                cssClass: 'ksa-login-checkbox',
                dataField: 'useLdap',
                visible: KsaLoginConfig.allowLDAP(),
                label: {
                    visible: false
                },
                editorType: 'dxCheckBox',
                editorOptions: {
                    'text': formatMessage("KsaLoginId.Form.UseLdap")
                }
            }, {
                itemType: 'simple',
                cssClass: 'ksa-login-checkbox',
                dataField: 'useForm',
                visible: false,
                label: {
                    visible: false
                },
                editorType: 'dxCheckBox',
                editorOptions: {
                    'text': formatMessage("KsaLoginId.Form.UseForm")
                }
            }, {
                itemType: 'group',
                cssClass: 'ksa-login-dbms-group ksa-group-collapsed',
                name: 'dbmsGroup',
                caption: formatMessage("KsaLoginId.Form.ConnectionSettings"),
                visible: !KsaLoginConfig.useAuthorizationService() && KsaLoginConfig.allowConnectionSettings() && !KsaLoginConfig.useDemoMode(),
                items: [{
                    itemType: 'simple',
                    dataField: 'dbmsType',
                    cssClass: 'ksa-login-input',
                    label: {
                        text: formatMessage('KsaLoginId.Form.DBMS')
                    },
                    editorType: 'dxSelectBox',
                    editorOptions: {
                        height: 56,
                        dataSource: {
                            store: new ArrayStore({
                                data: KsaLoginConfig.dbmsTypes()
                            })
                        },
                        displayExpr: 'value',
                        valueExpr: 'key',
                        placeholder: '',
                        onEnterKey: self._loginStandard
                    }
                }, {
                    itemType: 'simple',
                    dataField: 'server',
                    cssClass: 'ksa-login-input',
                    label: {
                        text: formatMessage("KsaLoginId.Form.Server")
                    },
                    editorType: 'dxTextBox',
                    editorOptions: {
                        height: 56,
                        onEnterKey: self._loginStandard
                    },
                }, {
                    itemType: 'simple',
                    dataField: 'database',
                    cssClass: 'ksa-login-input',
                    label: {
                        text: formatMessage("KsaLoginId.Form.Database")
                    },
                    editorType: 'dxTextBox',
                    editorOptions: {
                        height: 56,
                        onEnterKey: self._loginStandard
                    }
                }]
            }],
            onInitialized: function (e) {
                self._loginForm = e.component;
                e.component.option('formData', self.authData);
            },
            onContentReady: function (e) {
                self._updateDbmsEditors(e.component);
            },
            onFieldDataChanged: function (e) {
                if (e.dataField === 'dbmsType') {
                    self._updateDbmsEditors(e.component);
                }
            }
        };

        /* Добавление кнопки входа с использованием введённых значений имени пользователя и пароля. */
        options.items.push({
            itemType: 'button',
            cssClass: 'ksa-login-button',
            horizontalAlignment: 'right',
            visible: !KsaLoginConfig.useDemoMode(),
            buttonOptions: {
                height: 56,
                width: '100%',
                text: formatMessage("KsaLoginId.Form.Login"),
                type: 'default',
                useSubmitBehavior: true,
                onClick: self._loginStandard
            }
        });

        /* Добавление разделителя кнопок "ИЛИ". */
        if ((KsaLoginConfig.allowGuestLogin() || KsaLoginConfig.allowOpenId() || KsaLoginConfig.allowESIA()) && !KsaLoginConfig.useDemoMode()) {
            options.items.push({
                itemType: 'simple',
                visible: true,
                template: function () {
                    return jquery('<div/>', {class: 'ksa-login-separator'}).html(formatMessage("KsaLoginId.Form.OrSeparator"));
                }
            });
        }

        /* Добавление кнопки гостевого входа. */
        if (KsaLoginConfig.allowGuestLogin() || KsaLoginConfig.useDemoMode()) {
            options.items.push({
                itemType: 'button',
                cssClass: KsaLoginConfig.useDemoMode() ? 'ksa-login-button' : 'ksa-login-button ksa-login-button-grey',
                visible: true,
                horizontalAlignment: 'right',
                buttonOptions: {
                    height: 56,
                    width: '100%',
                    text: formatMessage("KsaLoginId.Form.GuestLogin"),
                    type: KsaLoginConfig.useDemoMode() ? 'default' : 'normal',
                    useSubmitBehavior: true,
                    onClick: self._loginGuest
                }
            });
        }

        if (KsaLoginConfig.allowOpenId() && !KsaLoginConfig.useDemoMode()) {
            options.items.push({
                itemType: 'button',
                cssClass: 'ksa-login-button ksa-login-button-grey',
                horizontalAlignment: 'right',
                buttonOptions: {
                    height: 56,
                    width: "100%",
                    text: formatMessage("KsaLoginId.Form.KSIDM"),
                    type: "normal",
                    useSubmitBehavior: false,
                    onClick: self._loginKSIDM
                }
            });
        }

        /* Добавление кнопки входа с использованием авторизации через ЕСИА. */
        if (KsaLoginConfig.allowESIA() && !KsaLoginConfig.useDemoMode()) {
            options.items.push({
                itemType: 'button',
                cssClass: 'ksa-login-button ksa-login-button-grey',
                horizontalAlignment: 'right',
                buttonOptions: {
                    height: 56,
                    width: "100%",
                    icon: 'esia-logo',
                    text: formatMessage("KsaLoginId.Form.ESIA"),
                    type: "normal",
                    useSubmitBehavior: false,
                    onClick: self._loginESIA
                }
            });
        }

        return options;
    }

    /** Получение параметров всплывающей панели ожидания авторизации. */
    loadPanelOptions() {
        const self = this;
        return {
            container: '.ksa-load-panel-container',
            shadingColor: 'rgba(255, 255, 255, 0.7)',
            position: 'center',
            indicatorSrc: KsaLoginConfig.urls.loginIndicator(),
            visible: false,
            showIndicator: true,
            showPane: true,
            shading: true,
            hideOnOutsideClick: false,
            focusStateEnabled: false,
            height: 414,
            width: 440,
            maxHeight: 'calc(100vh - 12px)',
            maxWidth: 'calc(100vw - 12px)',
            message: formatMessage('KsaLoginId.LoginWaitMessage'),
            onInitialized(e) {
                self._loadPanel = e.component;
            }
        };
    }
}

/** Обновление состояния активности редакторов настройки параметров подключения.
 *  @param form{dxForm} форма авторизации */
KsaLoginViewModel.prototype._updateDbmsEditors = function(form) {
    if (!form.itemOption('dbmsGroup').visible) {
        return;
    }

    const isDefault = form.getEditor('dbmsType').option('value') === 'Default';

    form.getEditor('server'  ).option('disabled', isDefault)
    form.getEditor('database').option('disabled', isDefault)

    if (isDefault) {
        let formData = form.option('formData');
        form.updateData('server',   formData.defaultServer);
        form.updateData('database', formData.defaultDatabase);
    }
}

/** Свернуть/развернуть группу настроек подключения. */
KsaLoginViewModel.prototype._toggleDbmsOptions = function() {
    const groupItem    = jquery('.ksa-login-dbms-group');
    const groupContent = jquery('.ksa-login-dbms-group .dx-form-group-with-caption > .dx-form-group-content');
    const toExpand     = groupItem.hasClass('ksa-group-collapsed');

    groupItem.removeClass([ 'ksa-group-expanded', 'ksa-group-collapsed' ]);
    groupItem.addClass(toExpand ? 'ksa-group-expanded': 'ksa-group-collapsed');
    groupContent.css({
        height: toExpand ? groupContent.prop('scrollHeight') : 0,
        opacity: toExpand ? 1 : 0
    });
}

/** Добавление обработчиков событий. */
KsaLoginViewModel.prototype._setEventHandlers = function() {
    const self = this;

    document.addEventListener('click', function(e) {
        if (e.target.closest('.ksa-login-dbms-group') != null && e.target.classList.contains('dx-form-group-caption')) {
            e.stopPropagation();
            self._toggleDbmsOptions();
        }
    });

    /** Применение цвета автозаполнения к оформлению виджета.
     *  @param node{HTMLElement} элемент со включенным автозаполнением;
     *  @param isAutocompleted{boolean} признак автозаполнения. */
    const updateBackgroundColor = function (node, isAutocompleted) {
        if (node) {
            const widget = node.closest('.dx-texteditor.dx-widget');
            if (widget) {
                widget.style.backgroundColor = isAutocompleted
                    ? getComputedStyle(node).getPropertyValue('background-color')
                    : '';
            }
        }
    };

    /** Обработка событий включения/отключения признака автозаполнения. */
    document.addEventListener('onautocomplete', function(e) {
        const isAutocompleted = e.target.hasAttribute('autocompleted');
        updateBackgroundColor(e.target, isAutocompleted);
    });

    /* Обеспечение обновления стиля автоматически заполненных виджетов при первом
     * отображении страницы, в случае если событие 'onautocomplete' не было сгенерировано. */
    let attempt = 0;
    const timer = setInterval(function () {
        if (attempt >= 10) {
            clearTimeout(timer);
        }
        const nodes = document.querySelectorAll('input:-webkit-autofill');
        if (nodes.length > 0) {
            nodes.forEach(function(node) { updateBackgroundColor(node, true); });
            clearTimeout(timer);
        }
        ++attempt;
    }, 100);
};

/** Выполнение стандартного входа с использованием введённых данных. */
KsaLoginViewModel.prototype._loginStandard = function() {
    const self = this;
    self._executeLoginInternal({ useGuestLogin: false });
};

/** Выполнение входа под гостевым пользователем. */
KsaLoginViewModel.prototype._loginGuest = function() {
    const self = this;
    self._executeLoginInternal({ useGuestLogin: true });
};

/** Выполнение входа с авторизацией через КС IDM. */
KsaLoginViewModel.prototype._loginKSIDM = function() {
    const self = this;
    window.location.replace(KsaLoginConfig.urls.loginKsIdmAction());
};

/** Выполнение входа с авторизацией через ЕСИА. */
KsaLoginViewModel.prototype._loginESIA = function() {
    const self = this;
    jquery.ajax({
        url: KsaLoginConfig.urls.loginEsiaAction(),
        type: 'POST',
        data: {
            origin: location.origin
        },
        success: function (response) {
            window.location = response.AuthentificationUrl;
        },
        error: function (jqXHR) {
            self.loginError.showError({
                errorType: jqXHR.status === 0 ? 'connect' : 'auth',
                errorMessage: jqXHR.status === 0 ? formatMessage('KsaLoginId.Error.ServerUnavailable') : jqXHR.responseText
            });
        }
    });
};

/** Фактическая авторизация.
 *  @param authModelOverrides{object} переопределения значений модели. */
KsaLoginViewModel.prototype._executeLoginInternal = function(authModelOverrides) {
    const self = this;
    const authModel = jquery.extend(true, {}, self.authData || {}, authModelOverrides);

    let onAfterLogin = function () {
        localStorage.setItem('login', authModel.login);
        window.location.replace(KsaLoginConfig.urls.homePage());
    };

    let logout = function () {
        jquery.ajax({
            url: KsaLoginConfig.urls.logoutAction(),
            type: 'POST',
            success: function () {
                window.location.replace(KsaLoginConfig.urls.loginPage());
            }
        });
    };

    let ajaxErrorCallback = function (jqXHR) {
        self._loadPanel?.hide();
        self.loginError.showError({
            errorType: jqXHR.status === 0 ? 'connect' : 'auth',
            errorMessage: jqXHR.status === 0 ? formatMessage('KsaLoginId.Error.ServerUnavailable') : jqXHR.responseText
        });
    };

    let changePassword = function (data) {
        self._loadPanel?.show()
        jquery.ajax({
            url: KsaLoginConfig.urls.changePasswordAction(),
            type: 'POST',
            contentType: "application/json",
            data: JSON.stringify(jquery.extend(true, {}, authModel, { changePasswordData: data })),
            success: onAfterLogin,
            error: ajaxErrorCallback
        });
    };

    self._loadPanel?.show()
    jquery.ajax({
        url: KsaLoginConfig.urls.loginAction(),
        type: 'POST',
        contentType: "application/json",
        data: JSON.stringify(authModel),
        success: function (response) {
            if (response && (response.byDbmsDemand || response.byLogin)) {
                self._loadPanel?.hide();
                self.changePassword.showDialog({
                    authModel: authModel,
                    changePasswordData: response,
                    changePasswordCallback: changePassword,
                    cancelCallback: logout
                });
            }
            else {
                onAfterLogin();
            }
        },
        error: ajaxErrorCallback
    });
};

/* Применение локализации. */
await localizeKsaLoginAsync(KsaLoginConfig.culture(), KsaLoginConfig.urls.loginLocalization());

readyCallbacks.add(function() {
    ko.applyBindings(new KsaLoginViewModel());
});
